/**
 * La grille de répartition, côté écran (ARTO-58) : trouver la version en
 * vigueur, bâtir les parts d'un brouillon, et en tirer un APERÇU des mois
 * provisoires. Rien d'ici n'écrit : l'enregistrement part vers l'API, qui
 * valide (somme, bénéficiaires, mois clôturés) et reste la seule source.
 *
 * Aucun nom de bénéficiaire, aucun pourcentage : tout vient de l'API. Les
 * parts voyagent en points de base (10 000 = 100 %), entiers.
 */
import { POINTS_DE_BASE, repartirAuPlusFortReste } from '@artosera/shared';
import type {
  ComptabiliteDirection,
  MoisComptable,
  TypeRecetteId,
  VersionGrille,
} from '../../types.js';

// Le plus fort reste est celui de l'API, au caractère près : le même code,
// partagé (`@artosera/shared`).
export { repartirAuPlusFortReste };

/** Les parts d'un service : l'identifiant du bénéficiaire → ses points de base. */
export type Parts = Record<string, number>;

/** Les parts de chaque type qu'on a touché, hors celui qui prend le reste. */
export type Modifs = Partial<Record<TypeRecetteId, Parts>>;

/** Une exclusion applicable à un service, telle que l'API la rend (`services[].exclusions`). */
export interface ExclusionService {
  beneficiaireId: string;
  galerieId: string;
  htCentimes: number;
}

/**
 * Répartit le HT d'un service en tenant compte des exclusions de galeries,
 * comme l'API (`repartirService`) : le HT de chaque galerie exclue est
 * regroupé par ensemble de bénéficiaires qui l'excluent, le reste du HT forme
 * un groupe sans exclusion ; dans chaque groupe, la part de qui exclut va au
 * bénéficiaire du reste ; chaque groupe se répartit au plus fort reste, et les
 * montants s'additionnent. `null` quand une part serait à transférer sans
 * bénéficiaire du reste : le service n'est alors pas réparti. Sert l'aperçu.
 */
export function repartirAvecExclusions(
  htCentimes: number,
  parts: ReadonlyArray<{ beneficiaireId: string; partPointsDeBase: number }>,
  exclusions: readonly ExclusionService[],
  resteId: string | null,
): Map<string, number> | null {
  const galeries = new Map<string, { exclus: Set<string>; ht: number }>();
  for (const exclusion of exclusions) {
    const galerie = galeries.get(exclusion.galerieId) ?? { exclus: new Set(), ht: 0 };
    galerie.exclus.add(exclusion.beneficiaireId);
    // Le HT de la galerie dans le service : le même pour chacun de ceux qui l'excluent.
    galerie.ht = exclusion.htCentimes;
    galeries.set(exclusion.galerieId, galerie);
  }
  const groupes = new Map<string, { exclus: Set<string>; ht: number }>();
  let exclu = 0;
  for (const galerie of galeries.values()) {
    const cle = [...galerie.exclus].sort().join(',');
    const groupe = groupes.get(cle) ?? { exclus: galerie.exclus, ht: 0 };
    groupe.ht += galerie.ht;
    groupes.set(cle, groupe);
    exclu += galerie.ht;
  }
  const libre = groupes.get('') ?? { exclus: new Set<string>(), ht: 0 };
  libre.ht += htCentimes - exclu;
  groupes.set('', libre);

  const montants = new Map<string, number>();
  for (const groupe of groupes.values()) {
    let transfert = 0;
    const vecteur = parts.map((part) => {
      if (
        part.beneficiaireId !== resteId &&
        groupe.exclus.has(part.beneficiaireId) &&
        part.partPointsDeBase > 0
      ) {
        transfert += part.partPointsDeBase;
        return { beneficiaireId: part.beneficiaireId, points: 0 };
      }
      return { beneficiaireId: part.beneficiaireId, points: part.partPointsDeBase };
    });
    if (transfert > 0) {
      if (resteId === null) {
        if (groupe.ht > 0) return null;
        continue;
      }
      const reste = vecteur.find((part) => part.beneficiaireId === resteId);
      if (reste) reste.points += transfert;
      else vecteur.push({ beneficiaireId: resteId, points: transfert });
    }
    const repartis = repartirAuPlusFortReste(
      groupe.ht,
      vecteur.map((part) => part.points),
    );
    vecteur.forEach((part, rang) => {
      montants.set(
        part.beneficiaireId,
        (montants.get(part.beneficiaireId) ?? 0) + (repartis[rang] ?? 0),
      );
    });
  }
  return montants;
}

/** La version d'un service applicable à un mois : celle de plus grand départ qui ne le dépasse pas. */
export function versionEnVigueur(
  versions: readonly VersionGrille[],
  service: TypeRecetteId,
  mois: string,
): VersionGrille | null {
  let trouvee: VersionGrille | null = null;
  for (const version of versions) {
    if (version.service !== service || version.depuisMois > mois) continue;
    if (trouvee === null || version.depuisMois > trouvee.depuisMois) trouvee = version;
  }
  return trouvee;
}

/** Le départ de la première version d'un service strictement après un mois, ou `null`. */
export function departSuivant(
  versions: readonly VersionGrille[],
  service: TypeRecetteId,
  apres: string,
): string | null {
  let suivant: string | null = null;
  for (const version of versions) {
    if (version.service !== service || version.depuisMois <= apres) continue;
    if (suivant === null || version.depuisMois < suivant) suivant = version.depuisMois;
  }
  return suivant;
}

/** Les parts de la version en vigueur à un mois, ou `null` quand le service n'en a aucune. */
export function partsEnVigueur(
  versions: readonly VersionGrille[],
  service: TypeRecetteId,
  mois: string,
): Parts | null {
  const version = versionEnVigueur(versions, service, mois);
  if (version === null) return null;
  const parts: Parts = {};
  for (const part of version.parts) parts[part.beneficiaireId] = part.partPointsDeBase;
  return parts;
}

/** Ce que les parts d'un service donnent à chaque bénéficiaire, le reste compris. */
export function partsCompletes(
  modifie: Parts,
  resteId: string,
  beneficiaires: readonly string[],
): Parts {
  const parts: Parts = {};
  let somme = 0;
  for (const id of beneficiaires) {
    if (id === resteId) continue;
    const points = Math.max(0, Math.min(POINTS_DE_BASE, modifie[id] ?? 0));
    parts[id] = points;
    somme += points;
  }
  parts[resteId] = Math.max(0, POINTS_DE_BASE - somme);
  return parts;
}

/** Deux jeux de parts disent-ils la même chose ? Une part nulle vaut une part absente. */
export function memesParts(a: Parts | null, b: Parts | null): boolean {
  if (a === null || b === null) return a === b;
  const ids = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const id of ids) {
    if ((a[id] ?? 0) !== (b[id] ?? 0)) return false;
  }
  return true;
}

/** Le plafond d'une part : ce que les autres parts (hors reste) laissent sous 100 %. */
export function plafondDePart(parts: Parts, id: string, resteId: string): number {
  let autres = 0;
  for (const [cle, points] of Object.entries(parts)) {
    if (cle !== id && cle !== resteId) autres += points;
  }
  return Math.max(0, POINTS_DE_BASE - autres);
}

/**
 * Le corps d'une version : des parts explicites qui somment 10 000, dans
 * l'ordre du partage. Une part nulle n'est pas envoyée (elle ne ferait que
 * citer un bénéficiaire sans rien lui donner) ; il en reste toujours une.
 */
export function corpsDesParts(
  completes: Parts,
  ordre: readonly string[],
): Array<{ beneficiaireId: string; partPointsDeBase: number }> {
  return ordre
    .filter((id) => (completes[id] ?? 0) > 0)
    .map((id) => ({ beneficiaireId: id, partPointsDeBase: completes[id] ?? 0 }));
}

/** Le bénéficiaire qui prend le reste par défaut : celui qui reçoit le plus des versions en vigueur. */
export function resteParDefaut(
  versions: readonly VersionGrille[],
  mois: string,
  beneficiaires: readonly string[],
): string | null {
  const cumul = new Map<string, number>();
  for (const service of new Set(versions.map((v) => v.service))) {
    const parts = partsEnVigueur(versions, service, mois);
    if (parts === null) continue;
    for (const [id, points] of Object.entries(parts)) cumul.set(id, (cumul.get(id) ?? 0) + points);
  }
  let meilleur: string | null = null;
  let max = -1;
  // Dans l'ordre du partage : à égalité, le premier garde le reste.
  for (const id of beneficiaires) {
    const points = cumul.get(id) ?? 0;
    if (points > max) {
      max = points;
      meilleur = id;
    }
  }
  return meilleur;
}

// ── L'aperçu des mois provisoires ───────────────────────────────────────

export interface EntreeApercu {
  /** Le premier mois où le brouillon s'applique. */
  depuis: string;
  /** Les parts COMPLÈTES des types modifiés. */
  parts: Partial<Record<TypeRecetteId, Parts>>;
  versions: readonly VersionGrille[];
  /** Les identifiants des bénéficiaires, dans l'ordre du partage. */
  ordre: readonly string[];
  libelles: ReadonlyMap<string, string>;
}

function rangDans(ordre: readonly string[]): (id: string) => number {
  return (id) => {
    const position = ordre.indexOf(id);
    return position === -1 ? ordre.length : position;
  };
}

function recalculerMois(
  mois: MoisComptable,
  ordre: readonly string[],
  libelles: ReadonlyMap<string, string>,
): MoisComptable {
  const cumul = new Map<string, { libelle: string; montantCentimes: number }>();
  for (const service of mois.services) {
    for (const part of service.parts) {
      const ligne = cumul.get(part.beneficiaireId) ?? {
        libelle: libelles.get(part.beneficiaireId) ?? part.libelle,
        montantCentimes: 0,
      };
      ligne.montantCentimes += part.montantCentimes;
      cumul.set(part.beneficiaireId, ligne);
    }
  }
  const rang = rangDans(ordre);
  return {
    ...mois,
    // Sans version, ou sans parts (une exclusion sans bénéficiaire du reste).
    nonReparti: mois.services
      .filter(
        (service) =>
          service.htCentimes > 0 && (service.regle === null || service.parts.length === 0),
      )
      .map((service) => service.service),
    parBeneficiaire: [...cumul.entries()]
      .sort(([a], [b]) => rang(a) - rang(b))
      .map(([beneficiaireId, ligne]) => ({ beneficiaireId, ...ligne })),
  };
}

/**
 * La comptabilité telle qu'elle serait si le brouillon était enregistré : les
 * mois PROVISOIRES à partir de `depuis`, jusqu'à la version suivante de chaque
 * service. Un mois clôturé ne bouge jamais. Les totaux de la période suivent.
 */
export function appliquerApercu(
  donnees: ComptabiliteDirection,
  entree: EntreeApercu,
): ComptabiliteDirection {
  if (Object.keys(entree.parts).length === 0) return donnees;

  const mois = donnees.mois.map((courant) => {
    if (courant.statut === 'cloture' || courant.mois < entree.depuis) return courant;
    let change = false;
    const lignes = courant.services.map((ligne) => {
      const parts = entree.parts[ligne.service];
      if (parts === undefined) return ligne;
      const suivant = departSuivant(entree.versions, ligne.service, entree.depuis);
      if (suivant !== null && courant.mois >= suivant) return ligne;
      change = true;
      const ids = entree.ordre.filter((id) => (parts[id] ?? 0) > 0);
      const resteId = courant.resteId ?? null;
      const montants = repartirAvecExclusions(
        ligne.htCentimes,
        ids.map((id) => ({ beneficiaireId: id, partPointsDeBase: parts[id] ?? 0 })),
        ligne.exclusions ?? [],
        resteId,
      );
      const lignesParts =
        montants === null
          ? []
          : ids.map((id) => ({
              beneficiaireId: id,
              libelle: entree.libelles.get(id) ?? id,
              partPointsDeBase: parts[id] ?? 0,
              montantCentimes: montants.get(id) ?? 0,
            }));
      // Le bénéficiaire du reste hors de la version : à 0 point, avec ce qu'il reçoit.
      const transfere = resteId === null ? 0 : (montants?.get(resteId) ?? 0);
      if (resteId !== null && transfere > 0 && !ids.includes(resteId)) {
        lignesParts.push({
          beneficiaireId: resteId,
          libelle: entree.libelles.get(resteId) ?? resteId,
          partPointsDeBase: 0,
          montantCentimes: transfere,
        });
      }
      return {
        ...ligne,
        regle: { id: 'apercu', depuisMois: entree.depuis },
        parts: lignesParts,
      };
    });
    return change
      ? recalculerMois({ ...courant, services: lignes }, entree.ordre, entree.libelles)
      : courant;
  });

  const cumul = new Map<string, { libelle: string; montantCentimes: number }>();
  for (const m of mois) {
    for (const ligne of m.parBeneficiaire) {
      const total = cumul.get(ligne.beneficiaireId) ?? {
        libelle: ligne.libelle,
        montantCentimes: 0,
      };
      total.montantCentimes += ligne.montantCentimes;
      cumul.set(ligne.beneficiaireId, total);
    }
  }
  const rang = rangDans(entree.ordre);
  return {
    ...donnees,
    mois,
    totaux: {
      ...donnees.totaux,
      parBeneficiaire: [...cumul.entries()]
        .sort(([a], [b]) => rang(a) - rang(b))
        .map(([beneficiaireId, ligne]) => ({ beneficiaireId, ...ligne })),
    },
  };
}
